import { match } from '@formatjs/intl-localematcher';

import type { Locale } from '../../../i18n-config';
import { i18n } from '../../../i18n-config';

/**
 * Detects the browser's preferred locale using intelligent matching.
 * Uses @formatjs/intl-localematcher for proper locale negotiation.
 * Handles locale variants (e.g., en-US → en, de-AT → de) and fallbacks.
 *
 * @param acceptLanguageHeader - Optional Accept-Language header string (for SSR)
 * @returns The browser locale if supported, otherwise the default locale
 */
export function detectBrowserLocale(acceptLanguageHeader?: string): Locale {
  if (typeof window === 'undefined' && !acceptLanguageHeader) {
    return i18n.defaultLocale;
  }

  // Get browser languages - either from Accept-Language header or navigator
  const requestedLocales: string[] = acceptLanguageHeader
    ? // Parse Accept-Language header (simple parsing, negotiator would be better for complex cases)
      acceptLanguageHeader
        .split(',')
        .map((lang) => lang.split(';')[0]?.trim())
        .filter((lang): lang is string => Boolean(lang))
    : // Use navigator.languages (includes all preferred languages, not just the first)
      navigator.languages.length > 0
      ? [...navigator.languages] // Convert readonly array to mutable
      : [navigator.language];

  try {
    // Use intl-localematcher for proper locale negotiation
    // This handles locale variants, fallbacks, and follows Unicode CLDR matching
    const matchedLocale = match(
      requestedLocales,
      [...i18n.locales], // Convert readonly array to mutable array
      i18n.defaultLocale,
    );

    return matchedLocale as Locale;
  } catch {
    // Fallback to default if matching fails
    return i18n.defaultLocale;
  }
}
